import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import { Footer } from '../components/Footer';
import { SEO } from '../components/seo/SEO';
import { craReassessmentResearch } from '../lib/craReassessmentResearch';

const SECTION_HEADING_CLASS =
  'text-[40px] leading-[1.02] sm:text-[54px] sm:leading-[0.98] lg:text-[64px] lg:leading-[0.96]';

function ResearchNav() {
  return (
    <header className="sticky top-0 z-50 border-b border-csai-ink/[0.06] bg-csai-warm-white/95 backdrop-blur-sm">
      <nav className="mx-auto flex h-14 max-w-7xl items-center justify-between px-6">
        <Link to="/" className="font-serif text-lg font-bold tracking-tight text-csai-ink">
          Situs Canada
        </Link>
        <div className="hidden items-center gap-7 md:flex">
          <a href="#research" className="text-xs font-medium tracking-wide text-csai-soft-ink hover:text-csai-ink">
            Research
          </a>
          <a href="#cra-guidance" className="text-xs font-medium tracking-wide text-csai-soft-ink hover:text-csai-ink">
            CRA guidance
          </a>
          <a href="#status" className="text-xs font-medium tracking-wide text-csai-soft-ink hover:text-csai-ink">
            Status
          </a>
        </div>
        <Link to={craReassessmentResearch.researchCta.href} className="hidden sm:block">
          <Button size="sm" variant="primary">
            {craReassessmentResearch.researchCta.label}
          </Button>
        </Link>
      </nav>
    </header>
  );
}

export function CraReassessmentResponse() {
  const content = craReassessmentResearch;

  return (
    <div className="flex min-h-screen flex-col bg-csai-warm-white">
      <SEO
        title={content.title}
        description={content.description}
        canonical={content.canonicalUrl}
        includeSiteName={false}
      />
      <ResearchNav />

      <main className="flex-grow">
        <section className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8">
          <div className="max-w-4xl">
            <p className="mb-5 text-label text-csai-warm-muted">{content.stageLabel}</p>
            <h1 className={`${SECTION_HEADING_CLASS} mb-6 text-csai-ink`}>{content.headline}</h1>
            <p className="max-w-3xl text-body-lg leading-8 text-csai-soft-ink">{content.coreClaim}</p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-start">
              <Link to={content.researchCta.href} className="w-full sm:w-auto">
                <Button size="lg" className="h-12 w-full px-6 text-base sm:w-auto" variant="primary">
                  {content.researchCta.label}
                </Button>
              </Link>
              <a
                href={content.discoveryCta.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button variant="secondary" size="lg" className="h-12 w-full px-6 text-base sm:w-auto">
                  {content.discoveryCta.label}
                </Button>
              </a>
            </div>
          </div>
        </section>

        <section id="research" className="border-y border-csai-ink/[0.08] bg-csai-paper">
          <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:px-8">
            <div>
              <p className="mb-4 text-label text-csai-warm-muted">Research boundaries</p>
              <h2 className={`${SECTION_HEADING_CLASS} text-csai-ink`}>A synthetic workflow, under review</h2>
            </div>
            <div className="space-y-8">
              <ul className="grid gap-3 text-body-lg leading-8 text-csai-ink">
                {content.boundaries.map((boundary) => (
                  <li key={boundary} className="border-l-2 border-csai-navy pl-4">
                    {boundary}
                  </li>
                ))}
              </ul>
              <div>
                <h3 className="text-xl font-semibold text-csai-ink">What we are testing</h3>
                <ul className="mt-4 grid gap-3 leading-7 text-csai-soft-ink">
                  {content.researchComponents.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-csai-ink">What we want CPA feedback on</h3>
                <ul className="mt-4 grid gap-3 leading-7 text-csai-soft-ink">
                  {content.feedbackQuestions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="cra-guidance" className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="max-w-4xl">
            <p className="mb-4 text-label text-csai-warm-muted">CRA procedural information</p>
            <h2 className={`${SECTION_HEADING_CLASS} mb-5 text-csai-ink`}>
              What should a Canadian CPA collect before reviewing a CRA reassessment?
            </h2>
            <p className="text-body-lg leading-8 text-csai-soft-ink">
              This checklist summarizes public CRA procedural information. It does not describe an Orca product capability.
            </p>
          </div>
          <ol className="mt-12 grid gap-5 md:grid-cols-2">
            {content.checklist.map((item, index) => (
              <li key={item.title} className="border-t border-csai-ink/[0.12] py-6">
                <span className="text-sm font-semibold text-csai-navy">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="mt-4 text-xl font-semibold leading-snug text-csai-ink">{item.title}</h3>
                <p className="mt-3 leading-7 text-csai-soft-ink">{item.body}</p>
              </li>
            ))}
          </ol>
          <p className="mt-10 max-w-4xl border-l-2 border-csai-navy pl-4 leading-7 text-csai-soft-ink">
            {content.informationNotice}
          </p>
        </section>

        <section className="border-y border-csai-ink/[0.08] bg-csai-paper">
          <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
            <p className="mb-4 text-label text-csai-warm-muted">Official CRA sources</p>
            <h2 className="text-3xl font-semibold text-csai-ink sm:text-4xl">Read the current CRA guidance</h2>
            <ul className="mt-8 grid gap-4">
              {content.sources.map((source) => (
                <li key={source.url}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-semibold text-csai-navy underline underline-offset-4 hover:text-csai-primary-hover"
                  >
                    {source.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="max-w-4xl">
            <p className="mb-4 text-label text-csai-warm-muted">Practical workflow guidance</p>
            <h2 className={`${SECTION_HEADING_CLASS} mb-5 text-csai-ink`}>Suggested working-file structure</h2>
            <p className="text-body-lg leading-8 text-csai-soft-ink">
              Practical workflow guidance, not a CRA requirement.
            </p>
          </div>
          <ol className="mt-12 grid gap-5 md:grid-cols-2">
            {content.workingFileStructure.map((item, index) => (
              <li key={item.title} className="border-t border-csai-ink/[0.12] py-6">
                <span className="text-sm font-semibold text-csai-navy">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="mt-4 text-xl font-semibold leading-snug text-csai-ink">{item.title}</h3>
                <p className="mt-3 leading-7 text-csai-soft-ink">{item.body}</p>
              </li>
            ))}
          </ol>
          <div className="mt-12 max-w-4xl border-t border-csai-ink/[0.12] pt-8">
            <h3 className="text-2xl font-semibold text-csai-ink">{content.missingDocumentLog.label}</h3>
            <p className="mt-3 leading-7 text-csai-soft-ink">{content.missingDocumentLog.description}</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {content.missingDocumentLog.fields.map((field) => (
                <li key={field} className="border-l-2 border-csai-navy pl-3 text-sm font-semibold text-csai-ink">
                  {field}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="status" className="mx-auto max-w-4xl px-6 py-20 text-center lg:px-8">
          <p className="mb-4 text-label text-csai-warm-muted">{content.stageLabel}</p>
          <h2 className="text-3xl font-semibold text-csai-ink sm:text-4xl">Product proof is not pilot-grade yet.</h2>
          <p className="mx-auto mt-5 max-w-2xl leading-7 text-csai-soft-ink">
            We are collecting CPA feedback before making stronger workflow or data-handling claims.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to={content.researchCta.href} className="w-full sm:w-auto">
              <Button size="lg" className="h-12 w-full px-6 text-base sm:w-auto" variant="primary">
                {content.researchCta.label}
              </Button>
            </Link>
            <a
              href={content.discoveryCta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <Button variant="secondary" size="lg" className="h-12 w-full px-6 text-base sm:w-auto">
                {content.discoveryCta.label}
              </Button>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
